<template>
  <transition name="modal-fade">
    <div v-if="isOpen" class="system-warning-overlay" @click="handleClose">
      <div :class="['system-warning-modal', { 'rtl': currentLanguage === 'ar' }]" @click.stop>
        <div class="warning-icon">⚠️</div>

        <h2 class="warning-title">{{ msg.title }}</h2>

        <div class="warning-content">
          <p class="warning-message">{{ msg.message }}</p>

          <div class="warning-date">
            {{ formatDate(shutdownDate) }}
          </div>

          <div class="warning-reason">
            <strong>{{ currentLanguage === 'ar' ? 'السبب:' : 'Grund:' }}</strong> {{ customWarningMessage }}
          </div>

          <div class="warning-notice">
            <p class="notice-action">{{ msg.action }}</p>
          </div>
        </div>

        <button class="warning-button" @click="handleClose">
          {{ msg.button }}
        </button>
      </div>
    </div>
  </transition>
</template>

<script>
export default {
  name: 'SystemWarningModal',
  props: {
    isOpen: {
      type: Boolean,
      default: false
    },
    shutdownDate: {
      type: String,
      default: null
    },
    customWarningMessage: {
      type: String,
      default: 'Zahlung fällig'
    },
    currentLanguage: {
      type: String,
      default: 'de'
    }
  },
  computed: {
    messages() {
      return {
        de: {
          title: "Systemabschaltung Warnung",
          message: "Das System wird abgeschaltet am:",
          action: "Für weitere Informationen kontaktieren Sie bitte den Restaurantbesitzer oder Edjaas LLC.",
          button: "OK - Verstanden"
        },
        ar: {
          title: "تحذير إيقاف النظام",
          message: "سيتم إيقاف النظام في:",
          action: "للمزيد من المعلومات، اتصل بمالك المطعم أو Edjaas LLC.",
          button: "حسناً - مفهوم"
        }
      };
    },
    msg() {
      return this.messages[this.currentLanguage] || this.messages.de;
    }
  },
  methods: {
    formatDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${day}.${month}.${year} - ${hours}:${minutes}`;
    },
    handleClose() {
      // Save to localStorage that user dismissed the warning
      localStorage.setItem('systemWarningDismissed', 'true');
      this.$emit('close');
    }
  }
};
</script>

<style scoped>
.modal-fade-enter-active, .modal-fade-leave-active {
  transition: opacity 0.3s ease;
}

.modal-fade-enter, .modal-fade-leave-to {
  opacity: 0;
}

.system-warning-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.85);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 99999;
}

.system-warning-modal {
  background: var(--ods-surface);
  border-radius: var(--ods-radius-lg);
  padding: 40px;
  max-width: 550px;
  width: 90%;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  animation: slideUp 0.4s ease;
  text-align: center;
}

@keyframes slideUp {
  from {
    transform: translateY(50px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

.system-warning-modal.rtl {
  direction: rtl;
  font-family: inherit;
}

.warning-icon {
  font-size: 72px;
  margin-bottom: 20px;
}

.warning-title {
  font-size: 28px;
  font-weight: 700;
  color: #dc3545;
  margin-bottom: 25px;
}

.warning-content {
  margin-bottom: 30px;
}

.warning-message {
  font-size: 18px;
  color: #495057;
  margin-bottom: 20px;
  font-weight: 500;
}

.warning-date {
  font-size: 32px;
  font-weight: 700;
  color: var(--ods-ink);
  background: linear-gradient(135deg, #dc3545 0%, #c82333 100%);
  padding: 20px;
  border-radius: var(--ods-radius);
  margin: 25px 0;
  border: 3px solid #dc3545;
}

.warning-reason {
  font-size: 18px;
  color: #721c24;
  background: #f8d7da;
  padding: 15px;
  border-radius: var(--ods-radius);
  margin: 20px 0;
  border-left: 4px solid #dc3545;
}

.warning-reason strong {
  display: inline-block;
  margin-right: 8px;
}

.warning-notice {
  font-size: 16px;
  color: #856404;
  background: #fff3cd;
  padding: 20px;
  border-radius: var(--ods-radius);
  margin-top: 25px;
  line-height: 1.6;
  font-weight: 600;
  border: 2px solid #ffc107;
}

.notice-warning {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin-bottom: 15px;
}

.notice-warning i {
  font-size: 24px;
  color: #856404;
  flex-shrink: 0;
}

.notice-warning span {
  font-weight: 700;
  font-size: 16px;
}

.notice-action {
  font-size: 15px;
  line-height: 1.6;
  margin: 0;
}

.automated-message {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-top: 20px;
  padding: 12px;
  background: #e9ecef;
  border-radius: var(--ods-radius);
  font-size: 14px;
  color: var(--ods-ink-3);
  font-style: italic;
}

.automated-message i {
  font-size: 16px;
  color: var(--ods-ink-3);
}

.warning-button {
  background: linear-gradient(135deg, #dc3545 0%, #c82333 100%);
  color: var(--ods-ink);
  border: none;
  padding: 16px 48px;
  border-radius: var(--ods-radius);
  font-size: 18px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(220, 53, 69, 0.3);
  width: 100%;
  max-width: 300px;
}

.warning-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(220, 53, 69, 0.4);
}

.warning-button:active {
  transform: translateY(0);
}

/* RTL adjustments */
.system-warning-modal.rtl .warning-reason strong {
  margin-right: 0;
  margin-left: 8px;
}

.system-warning-modal.rtl .notice-warning {
  flex-direction: row-reverse;
}

.system-warning-modal.rtl .automated-message {
  flex-direction: row-reverse;
}
</style>
